import {
  Issue as DomainIssue,
  useGetIssueMessage,
  useIssue,
} from '@gripp/shared-logic';
import {
  ColumnData,
  SideChatPanel,
  SlidePanelRegistrar,
  useSlidePanel,
} from '@gripp/shared-ui';
import { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { AssetRouteProvider } from '../../assets/assetRouteContext';
import { AssetCard } from '../../assets/components/assetCard';
import { AssetChatHeaderButton } from '../../assets/components/assetChatHeaderButton';
import { AssetFilters } from '../../assets/components/assetFilters';
import { Issue } from '../../issues/components/issue';
import { useRoutineImagePanel } from '../../issues/components/useRoutineImagePanel';
import { MediaPicker } from '../../shared/components/media/mediaPicker';
import { getMediaBaseUrl } from '../../shared/mediaUtils';

const ACTIVITY_CONVERSATION_PANEL_SOURCE =
  'gripp-workspace/apps/gripp-web/src/activity/components/activityConversationPanel.tsx';
const ACTIVITY_ROUTES_SOURCE =
  'gripp-workspace/apps/gripp-web/src/activity/routes.tsx';
const ISSUE_COMPONENT_SOURCE =
  'gripp-workspace/apps/gripp-web/src/issues/components/issue.tsx';
const ASSET_CARD_COMPONENT_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/components/assetCard.tsx';
const SIDE_CHAT_PANEL_COMPONENT_SOURCE =
  'gripp-workspace/libs/shared-ui/src/components/issue/sideChatPanel.tsx';
const ACTIVITY_CONVERSATION_ROUTE_PATTERN = '/activities/:issueId';
const ACTIVITY_ASSET_ROUTE_PATTERN = '/activities/:issueId/asset/:assetId';
const ACTIVITY_SIDE_CHAT_ROUTE_PATTERN =
  '/activities/:issueId/sidechat/:messageId';

export const ActivityConversationPanel = () => {
  const ctx = useOutletContext<SlidePanelRegistrar | null>();

  if (!ctx?.setSlidePanel) {
    return <ActivityConversationPanelLegacy />;
  }

  return <ActivityConversationPanelShell />;
};

const ActivityConversationPanelShell = () => {
  const { issueId, assetId, messageId } = useParams();
  const navigate = useNavigate();

  // Side Chat (plan 0022 §C): the panel is rendered from the URL param, so the
  // issue + origin message are re-fetched here and handed to SideChatPanel. Both
  // hooks self-guard on a truthy id (enabled), so they no-op unless a side chat
  // is open.
  const { issue: sideChatIssue } = useIssue(messageId ? issueId : undefined);
  const { message: originMessage } = useGetIssueMessage(messageId);

  // Routine media opens as its own column beside the submission being reviewed.
  const { onViewImages, imageColumn } = useRoutineImagePanel(issueId);

  const rightColumn = useMemo<ColumnData | null>(() => {
    if (!issueId) return null;
    return {
      title: 'activities.detail.title',
      showHeader: false,
      disableScroll: true,
      // Wrap in AssetRouteProvider so Issue.onIssueDelete can navigate back
      // to /activities via listPath rather than relying on URL string checks.
      mainComponent: (
        <AssetRouteProvider value={{
          basePath: `/activities/${issueId}`,
          listPath: '/activities',
          isWorkspaceMode: false,
          issueId,
        }}>
          <Issue
            isComingFromActivities
            showAssetLink={!assetId}
            onViewImages={onViewImages}
          />
        </AssetRouteProvider>
      ),
      debug: {
        id: 'activity-conversation:right-issue',
        titleKey: 'activities.detail.title',
        component: 'Issue',
        componentSource: ISSUE_COMPONENT_SOURCE,
        columnSource: ACTIVITY_CONVERSATION_PANEL_SOURCE,
        ownerComponent: 'ActivityConversationPanelShell',
        ownerSource: ACTIVITY_CONVERSATION_PANEL_SOURCE,
        routePanelKey: 'activity-conversation',
        routePattern: assetId
          ? ACTIVITY_ASSET_ROUTE_PATTERN
          : ACTIVITY_CONVERSATION_ROUTE_PATTERN,
        routeSource: ACTIVITY_ROUTES_SOURCE,
        source: {
          owner: {
            file: ACTIVITY_CONVERSATION_PANEL_SOURCE,
            symbol: 'ActivityConversationPanelShell',
          },
          column: {
            file: ACTIVITY_CONVERSATION_PANEL_SOURCE,
            symbol: 'rightColumn',
          },
          component: {
            file: ISSUE_COMPONENT_SOURCE,
            symbol: 'Issue',
          },
          route: {
            file: ACTIVITY_ROUTES_SOURCE,
            symbol: 'activityRoutes',
          },
        },
        entity: { type: 'activity', id: issueId },
      },
    };
  }, [issueId, assetId, onViewImages]);

  const assetColumn = useMemo<ColumnData | null>(() => {
    if (!assetId) return null;
    return {
      title: 'assets.menu.titleSingular',
      showHeader: true,
      showBackButton: true,
      onBackButtonPress: () => navigate(`/activities/${issueId}`),
      // The third composition root that renders the asset profile header
      // (/activities/:issueId/asset/:assetId). The chat entry point has to be
      // on all three or it is missing depending on how the user arrived —
      // reaching an asset from an activity showed no icon while the asset
      // list showed one. AssetChatHeaderButton gates itself, including the
      // guest case, so no gate is duplicated here.
      headerComponent: (
        <View style={styles.assetHeaderActions}>
          <AssetChatHeaderButton assetId={assetId} />
          <AssetFilters assetId={assetId} issueId={issueId} />
        </View>
      ),
      disableScroll: false,
      // Wrap in AssetRouteProvider so fold components inside AssetCard
      // can read issueId and basePath via useAssetRouting(). They can't
      // use useParams() here because AssetCard is rendered outside the
      // <Outlet> that provides the child route context.
      mainComponent: (
        <AssetRouteProvider value={{
          basePath: `/activities/${issueId}/asset/${assetId}`,
          listPath: '/activities',
          isWorkspaceMode: false,
          issueId,
        }}>
          <AssetCard assetId={assetId} />
        </AssetRouteProvider>
      ),
      debug: {
        id: 'activity-conversation:third-asset-card',
        titleKey: 'assets.menu.titleSingular',
        component: 'AssetCard',
        componentSource: ASSET_CARD_COMPONENT_SOURCE,
        columnSource: ACTIVITY_CONVERSATION_PANEL_SOURCE,
        ownerComponent: 'ActivityConversationPanelShell',
        ownerSource: ACTIVITY_CONVERSATION_PANEL_SOURCE,
        routePanelKey: 'activity-asset-card',
        routePattern: ACTIVITY_ASSET_ROUTE_PATTERN,
        routeSource: ACTIVITY_ROUTES_SOURCE,
        source: {
          owner: {
            file: ACTIVITY_CONVERSATION_PANEL_SOURCE,
            symbol: 'ActivityConversationPanelShell',
          },
          column: {
            file: ACTIVITY_CONVERSATION_PANEL_SOURCE,
            symbol: 'thirdColumn',
          },
          component: {
            file: ASSET_CARD_COMPONENT_SOURCE,
            symbol: 'AssetCard',
          },
          route: {
            file: ACTIVITY_ROUTES_SOURCE,
            symbol: 'activityRoutes',
          },
        },
        entity: { type: 'asset', id: assetId },
      },
    };
  }, [assetId, issueId, navigate]);

  const sideChatColumn = useMemo<ColumnData | null>(() => {
    if (!messageId || !issueId || !sideChatIssue || !originMessage) return null;
    const backToConversation = () => navigate(`/activities/${issueId}`);
    return {
      title: 'activity.sideChat.title',
      rawTitle: 'Side Chat',
      // SideChatPanel renders its own header (back + orange title + participant
      // avatars, ported from gripp-os), so the column shell header is suppressed
      // — mirrors how the Issue right column owns its header (showHeader: false).
      showHeader: false,
      disableScroll: true,
      onBackButtonPress: backToConversation,
      mainComponent: (
        <SideChatPanel
          issue={sideChatIssue as DomainIssue}
          originMessage={originMessage}
          onBack={backToConversation}
          mediaPicker={MediaPicker}
          mediaUrl={getMediaBaseUrl()}
        />
      ),
      debug: {
        id: 'activity-conversation:side-chat',
        titleKey: 'activity.sideChat.title',
        rawTitle: 'Side Chat',
        component: 'SideChatPanel',
        componentSource: SIDE_CHAT_PANEL_COMPONENT_SOURCE,
        columnSource: ACTIVITY_CONVERSATION_PANEL_SOURCE,
        ownerComponent: 'ActivityConversationPanelShell',
        ownerSource: ACTIVITY_CONVERSATION_PANEL_SOURCE,
        routePanelKey: 'activity-sidechat',
        routePattern: ACTIVITY_SIDE_CHAT_ROUTE_PATTERN,
        routeSource: ACTIVITY_ROUTES_SOURCE,
        source: {
          owner: {
            file: ACTIVITY_CONVERSATION_PANEL_SOURCE,
            symbol: 'ActivityConversationPanelShell',
          },
          column: {
            file: ACTIVITY_CONVERSATION_PANEL_SOURCE,
            symbol: 'sideChatColumn',
          },
          component: {
            file: SIDE_CHAT_PANEL_COMPONENT_SOURCE,
            symbol: 'SideChatPanel',
          },
          route: {
            file: ACTIVITY_ROUTES_SOURCE,
            symbol: 'activityRoutes',
          },
        },
        entity: { type: 'sidechat', id: messageId },
      },
    };
  }, [messageId, issueId, sideChatIssue, originMessage, navigate]);

  // Column-slot math (plan 0022 §C): open panels fill `third` then `fourth` in
  // this order. The asset card keeps `third`; the side chat lands in `third` with
  // no asset open and `fourth` with one. Only two of the three can be open at
  // once and SlideLayout has no fifth slot — the asset and side chat routes are
  // mutually exclusive (neither carries the other's param), so in practice the
  // asset or the side chat takes `third` and a routine image takes `fourth`.
  const [thirdColumn = null, fourthColumn = null] = [
    assetColumn,
    sideChatColumn,
    imageColumn,
  ].filter((column): column is ColumnData => !!column);

  useSlidePanel('right', rightColumn, [rightColumn]);
  useSlidePanel('third', thirdColumn, [thirdColumn]);
  useSlidePanel('fourth', fourthColumn, [fourthColumn]);

  return null;
};

const ActivityConversationPanelLegacy = () => {
  const { issueId } = useParams();
  if (!issueId) return null;
  return (
    <AssetRouteProvider value={{
      basePath: `/activities/${issueId}`,
      listPath: '/activities',
      isWorkspaceMode: false,
      issueId,
    }}>
      <Issue isComingFromActivities />
    </AssetRouteProvider>
  );
};

const styles = StyleSheet.create({
  assetHeaderActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
});
